// @ts-strict-ignore
import { generate as generateId } from 'shortid';
import type { Status } from '@usealloy/component-library';

import { isResolvable } from './components/ResolutionModal/helper';
import type { FilterMatchesProps, MatchData } from './types';

export const getBadgeType = (status: string): Status => {
  if (status === 'True Match') return 'danger';
  if (status === 'Potential Match') return 'attention';
  // if Unknown or Needs Review
  return 'neutral';
};

export const getStrengthBadgeTooltip = (() => {
  const strengthTooltips = [
    /* 0.1 */ 'Fuzzy match with country mismatch',
    /* 0.2 */ 'Fuzzy match',
    /* 0.3 */ 'Fuzzy match with country match',
    /* 0.4 */ 'Fuzzy match with yob match',
    /* 0.5 */ 'Fuzzy match with yob and country match',
    /* 0.6 */ 'Exact match with country mismatch',
    /* 0.7 */ 'Exact match',
    /* 0.8 */ 'Exact match with country match',
    /* 0.9 */ 'Exact match with yob match',
    /* 1.0 */ 'Exact match with yob and country match',
  ];
  return (strength: number | null): string => (strengthTooltips[Math.floor(strength * 10) - 1]);
})();

export const getStrengthBadgeType = (service: string, strength: number | null): Status | null => {
  if (strength === null) return 'neutral';
  if (service === 'ComplyAdvantage') {
    // for Comply Advantage we care about the remainder, i.e. 0.1 and 1.1 will be treated the same, as will 0.9 and 1.9
    const strengthRemainder = strength % 1;
    if (strengthRemainder === 0 || strengthRemainder >= 0.7) {
      return 'danger';
    }
    if (strengthRemainder >= 0.4) {
      return 'attention';
    }
    return 'success';
  }
  // for Socure, we use a scale of 1-100
  if (strength >= 67) {
    return 'danger';
  }

  if (strength >= 34) {
    return 'attention';
  }

  return 'success';
};

export const getFilterOptions = (data: MatchData[]) => {
  const statuses = {};
  const statusOptions = [{ label: 'All', value: 'all' }];
  const types = {};
  const typeOptions = [{ label: 'All', value: 'all' }];
  const fields = {};
  const fieldOptions = [{ label: 'All', value: 'all' }];
  const screeningTypes = {};
  const screeningTypeOptions = [{ label: 'All', value: 'all' }];
  const services = {};
  const serviceOptions = [{ label: 'All', value: 'all' }];
  const aiRecommendations = {};
  const aiRecommendationOptions = [{ label: 'All', value: 'all' }];
  const suppressedOptions = [
    { label: 'All', value: 'all' },
    { label: 'Suppressed', value: 'suppressed' },
    { label: 'Unsuppressed', value: 'unsuppressed' },
  ];
  const strengthOptions = [ // the values here map to the corresponding CL badge colors for filtering
    { label: 'All', value: 'all' },
    { label: 'Unknown', value: 'neutral' },
    { label: 'Low', value: 'success' },
    { label: 'Medium', value: 'attention' },
    { label: 'High', value: 'danger' },
  ];
  data.forEach(datum => {
    const {
      match_fields: matchFields,
      match_types: matchTypes,
      status,
      screening_type: screeningType,
      service,
      ai_recommendation: aiRecommendation,
    } = datum;
    matchTypes.forEach(({ label }) => {
      if (!types[label]) {
        types[label] = true;
        typeOptions.push({ label, value: label });
      }
    });
    if (!statuses[status]) {
      statuses[status] = true;
      statusOptions.push({ label: status, value: status });
    }
    matchFields.forEach(field => {
      if (!fields[field]) {
        fields[field] = true;
        fieldOptions.push({ label: field, value: field });
      }
    });
    if (!screeningTypes[screeningType]) {
      screeningTypes[screeningType] = true;
      screeningTypeOptions.push({ label: screeningType, value: screeningType });
    }
    if (!services[service]) {
      services[service] = true;
      serviceOptions.push({ label: service, value: service });
    }
    if (aiRecommendation && !aiRecommendations[aiRecommendation]) {
      aiRecommendations[aiRecommendation] = true;
      aiRecommendationOptions.push({ label: aiRecommendation, value: aiRecommendation });
    }
  });
  return {
    fieldOptions,
    typeOptions,
    statusOptions,
    strengthOptions,
    suppressedOptions,
    screeningTypeOptions,
    serviceOptions,
    aiRecommendationOptions,
  };
};

export const filterMatches = ({
  filterState: {
    selectedFields,
    selectedLastUpdated,
    selectedStatuses,
    selectedStrengths,
    selectedSuppressed,
    selectedTypes,
    selectedScreeningTypes,
    selectedServices,
    selectedAiRecommendations,
  },
  matches,
}: FilterMatchesProps) => {
  let filteredMatches = [...matches];
  const mappedFields = selectedFields.map(({ value }) => value);
  const mappedStatuses = selectedStatuses ? selectedStatuses.map(({ value }) => value) : ['all'];
  const mappedStrengths = selectedStrengths.map(({ value }) => value);
  const mappedTypes = selectedTypes.map(({ value }) => value);
  const mappedScreeningTypes = selectedScreeningTypes.map(({ value }) => value);
  const mappedServices = selectedServices.map(({ value }) => value);
  const mappedAiRecommendations = selectedAiRecommendations ? selectedAiRecommendations.map(({ value }) => value) : ['all'];

  if (!mappedFields.includes('all')) {
    filteredMatches = filteredMatches.filter(({ match_fields: matchFields }) => (
      matchFields.filter(matchField => (
        mappedFields.includes(matchField)
      )).length > 0
    ));
  }
  if (!mappedStatuses.includes('all')) {
    filteredMatches = filteredMatches.filter(({ status }) => (
      mappedStatuses.includes(status)
    ));
  }
  if (!mappedTypes.includes('all')) {
    filteredMatches = filteredMatches.filter(({ match_types: matchTypes }) => (
      matchTypes.some(({ label }) => mappedTypes.includes(label))
    ));
  }
  if (!mappedScreeningTypes.includes('all')) {
    filteredMatches = filteredMatches.filter(({ screening_type: screeningType }) => (
      mappedScreeningTypes.includes(screeningType)
    ));
  }
  if (!mappedServices.includes('all')) {
    filteredMatches = filteredMatches.filter(({ service }) => (
      mappedServices.includes(service)
    ));
  }
  if (selectedSuppressed && selectedSuppressed.value !== 'all') {
    filteredMatches = filteredMatches.filter(({ is_suppressed: isSuppressed }) => (
      isSuppressed === (selectedSuppressed.value === 'suppressed')
    ));
  }
  if (selectedLastUpdated) {
    const [startDatetime, endDatetime] = selectedLastUpdated;
    filteredMatches = filteredMatches.filter(({ last_updated: lastUpdated }) => {
      const startValue = startDatetime?.toDate()?.getTime();
      const endValue = endDatetime?.toDate()?.getTime();
      const lastUpdatedValue = new Date(lastUpdated)?.getTime();
      return lastUpdatedValue >= startValue && lastUpdatedValue <= endValue;
    });
  } if (!mappedStrengths.includes('all')) {
    filteredMatches = filteredMatches.filter(({ match_strength, service }) => {
      const strengthCategory = getStrengthBadgeType(service, match_strength);
      return mappedStrengths.includes(strengthCategory);
    });
  }
  if (!mappedAiRecommendations.includes('all')) {
    filteredMatches = filteredMatches.filter(({ ai_recommendation: aiRecommendation }) => (
      mappedAiRecommendations.includes(aiRecommendation)
    ));
  }

  return filteredMatches;
};

export const allHandler = (prevSelections, newSelections) => {
  const prevArray = prevSelections.map(({ value }) => value);
  const newArray = newSelections.map(({ value }) => value);
  // when someone checks 'all' then other options should be deselected
  if (!prevArray.includes('all') && newArray.includes('all')) {
    return [{ label: 'All', value: 'all' }];
  }
  // if 'all' is already selected, deselect it when the user selects a new option
  if (prevArray.includes('all') && newArray.includes('all') && newArray.length > 1) {
    return newSelections.filter(({ value }) => value !== 'all');
  }
  // if nothing is selected, select 'all
  if (newArray.length === 0) {
    return [{ label: 'All', value: 'all' }];
  }
  return newSelections;
};

export const getSortingKeyString = sorting => {
  if (!sorting?.length) return '';
  const { id, desc } = sorting[0];
  return `${id}:${desc}`;
};

export const getAllSortedResults = (tableSorting, arr: MatchData[]): MatchData[] => {
  const { id: field, desc } = tableSorting[0];
  return [...arr].sort((_a, _b) => {
    const a = !desc ? _a : _b;
    const b = !desc ? _b : _a;
    switch (field) {
      case 'matchStrength': return a.match_strength - b.match_strength;
      case 'name': return a.name.localeCompare(b.name);
      case 'lastUpdated': return new Date(a.last_updated).getTime() - new Date(b.last_updated).getTime();
      default: return 0;
    }
  });
};

export const capitalize = (string: String, separator = ' ') => string
  .split(separator)
  .map(s => s.charAt(0).toUpperCase() + s.substring(1))
  .join(' ');

export const generateUnsupportedDatumDetails = (obj, parent = '') => {
  if (typeof obj !== 'object') return obj;
  const output = [];
  const keys = Object.keys(obj)
    .filter(key => !['name', 'links', 'url'].includes(key));
  if (!keys.length) return null;
  keys.forEach(key => {
    const value = obj[key];
    if (typeof value !== 'object') {
      output.push({
        label: capitalize(key, '_') + (parent ? ` (${parent})` : ''),
        values: [value],
      });
    } else if (Array.isArray(value)) {
      output.push({
        label: capitalize(key, '_'),
        values: value.map(val => generateUnsupportedDatumDetails(val)),
      });
    } else { // object
      const nestedObj = generateUnsupportedDatumDetails(value, `${capitalize(key, '_')}`);
      if (nestedObj && nestedObj.length) output.push(nestedObj[0]);
    }
  });
  return output.filter(e => e);
};

const generateListings = match => {
  const name = match.details?.name?.Full || '';
  const resp = {
    details: [
      {
        label: 'Name',
        values: [name],
      },
      ...generateUnsupportedDatumDetails(match.details),
    ],
    source: '',
    match_type: '',
    url: '',
  };
  if (!match.details?.links) {
    return match.details ? [resp] : [];
  }
  const links = match.details.links.filter(link => link);
  const media = links
    .map(url => ({
      url,
      date: null,
      title: '',
      snippet: '',
    }));

  const listing = {
    ...resp,
    media,
  };
  return [listing];
};

const stableSort = arr => [...arr].sort((a, b) => String(a).localeCompare(String(b)));

const normalizeListingDetails = details => stableSort(
  (details || []).map(detail => JSON.stringify({
    label: detail?.label || '',
    values: stableSort((detail?.values || []).map(String)),
  })),
);

const normalizeListings = listings => stableSort(
  (listings || []).map(listing => JSON.stringify({
    source: listing?.source || '',
    url: listing?.url || '',
    match_type: listing?.match_type || '',
    match_types_listed: stableSort((listing?.match_types_listed || []).map(String)),
    details: normalizeListingDetails(listing?.details),
  })),
);

const stableHash = (value = '') => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) - hash) + value.charCodeAt(index);
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }
  // eslint-disable-next-line no-bitwise
  return String(hash >>> 0);
};

export const getKey = match => {
  const name = match.name || match.details?.name?.Full || '';
  const service = match.details?.data_service || match.data_service || match.service || '';
  return `${service}-${name}`;
};

export const deDuplicateMatches = matches => { // ~InstantId duplicates others matches
  const uniqueMatches = {};
  matches.forEach(match => {
    const key = getKey(match);
    if (!uniqueMatches[key]) {
      uniqueMatches[key] = {};
    }
    const currentSize = match ? JSON.stringify(match).length : 0;
    uniqueMatches[key][currentSize] = match;
  });
  const resp = Object.values(uniqueMatches)
    .flatMap(cursor => Object.values(cursor));
  return resp;
};

export const normalizeMatches = response => {
  const {
    matches: rawMatches,
  } = response || {};

  const transformedMatches = rawMatches.map(match => {
    const service = match.details?.data_service
      || match.data_service // GIACT
      || match.details?.service
      || match.service;

    const listingsHash = stableHash(JSON.stringify(normalizeListings(match?.listings)));
    const navId = `${match.name}-${match.vendor_entity_identifier}-${match.match_strength}-${JSON.stringify(match.match_fields)}-${JSON.stringify(match.match_types)}-${match.screening_type}-${listingsHash}`;

    if (
      // Asserting that the supported matches will always be rendered by the SupportedMatchDetails component
      match.is_supported || isResolvable(match)
    ) {
      return {
        ...match,
        navigation_identifier: navId,
        is_supported: true,
      };
    }
    let reasons = match.reason_listed || [''];
    if (!Array.isArray(reasons)) reasons = [reasons];
    const matchTypes = reasons.map(reason => ({
      label: capitalize(reason, '-'),
      values: [reason],
    }));

    const name = match.details?.name?.Full || match.name || '-';
    const matchStrength = match.confidence || null;
    if (match.source) {
      if (!match.details) match.details = {};
      match.details.source = match.source;
    }

    return {
      ...match,
      name,
      screening_type: 'Source',
      match_types: matchTypes,
      match_fields: [],
      status: 'Read only',
      service,
      match_strength: matchStrength,
      last_updated: null,
      is_suppressed: null,
      listings: generateListings(match),
      is_resolvable: false,
      resolution: {
        note: null,
      },
      navigation_identifier: navId,
    };
  });
  return deDuplicateMatches(transformedMatches)
    .map(match => ({
      ...match,
      // pagination bugfix
      pagination_id: generateId(),
    }));
};

export const getDoMatchesMatch = (match1: MatchData, match2: MatchData) => (
  match1?.navigation_identifier === match2?.navigation_identifier
);

type AiResolverAssessment = {
  crimes_mentioned?: string[];
};

type AiResolverMatch = {
  match_details: {
    vendor_entity_identifier: string;
    vendor_search_identifier?: string;
    service?: string;
    name?: string;
  };
  match_status?: string;
  reason?: string;
  crime_severity?: [string, string][];
  assessments?: AiResolverAssessment[];
};

export type AiResolverResponse = {
  enhanced_matches: AiResolverMatch[];
};

const formatMatchStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    potential_match: 'Potential Match',
    no_match: 'No Match',
    false_positive: 'False Positive',
    true_match: 'True Match',
    unknown: 'Unknown',
  };
  return statusMap[status] || status;
};

const createMatchKeyFromParts = (args: {
  service?: string | null;
  name?: string | null;
  vendorEntityIdentifier?: string | null;
  vendorSearchIdentifier?: string | null;
}) => {
  const {
    service,
    name,
    vendorEntityIdentifier,
    vendorSearchIdentifier,
  } = args;

  if (!service || !name || !vendorEntityIdentifier) return null;

  const vendorSearchPart = vendorSearchIdentifier || 'null';

  return `${service.trim()}-${name.trim()}-${vendorEntityIdentifier.trim()}-${vendorSearchPart.trim()}`;
};


const createMatchKeyLlmEnhancedMatch = (match: AiResolverMatch) => createMatchKeyFromParts({
  service: match.match_details?.service,
  name: match.match_details?.name,
  vendorEntityIdentifier: match.match_details?.vendor_entity_identifier,
  vendorSearchIdentifier: match.match_details?.vendor_search_identifier,
});

const createMatchKey = (match: MatchData) => createMatchKeyFromParts({
  service: match.service,
  name: match.name,
  vendorEntityIdentifier: match.vendor_entity_identifier,
  vendorSearchIdentifier: match.vendor_search_identifier,
});

type AiRecommendationMapValue = {
  status: string;
  reason: string;
  crimeSeverity: [string, string][];
  aggregatedCrimes: string[];
};

const buildAiRecommendationMap = (aiResponse: AiResolverResponse | null) => {
  if (!aiResponse?.enhanced_matches?.length) return new Map<string, AiRecommendationMapValue>();

  const map = new Map<string, AiRecommendationMapValue>();
  aiResponse.enhanced_matches.forEach(item => {
    const key = createMatchKeyLlmEnhancedMatch(item);
    if (!key) return;

    const status = item.match_status ? formatMatchStatus(item.match_status) : undefined;
    const reason = item.reason ?? undefined;

    const crimeSeverity = item.crime_severity ?? [];
    const aggregatedCrimes = [];
    item.assessments?.forEach(assessment => {
      assessment.crimes_mentioned?.forEach(crime => {
        aggregatedCrimes.push(crime);
      });
    });

    if (!status) return;
    map.set(key, {
      status,
      reason: reason ?? '',
      crimeSeverity,
      aggregatedCrimes: Array.from(
        new Set(aggregatedCrimes.map(c => c?.trim()?.toLowerCase()).filter(Boolean)),
      ),
    });
  });
  return map;
};

export const mergeAiRecommendations = (
  matches: MatchData[],
  aiResponse: AiResolverResponse | null,
): MatchData[] => {
  if (!aiResponse?.enhanced_matches?.length) return matches;

  const aiRecommendationMap = buildAiRecommendationMap(aiResponse);

  return matches.map(match => {
    const key = createMatchKey(match);
    const aiData = key ? aiRecommendationMap.get(key) : undefined;

    return {
      ...match,
      ai_recommendation: aiData?.status ?? undefined,
      ai_reason: aiData?.reason ?? undefined,
      crime_severity: aiData?.crimeSeverity ?? undefined,
      aggregated_crimes: aiData?.aggregatedCrimes ?? undefined,
    };
  });
};
